// src/hooks/useAuth.ts
import { APP_CONFIG } from "config/appConfig";
import { useAppContext } from "context/appContext";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import keycloakService from "service/keycloakService";
import LocalStorageService from "service/localStorageService";
import { restoreData } from "store/actions";
import { useAuthStore } from "store/useAuthStore";
import { useResourceStore } from "store/useResourceStore";

/**
 * Auth hook - handles all authentication business logic
 * Store only manages state, this hook handles operations:
 * - Not Ready: Sign in (Keycloak redirect OR authService login)
 * - Sign out (provider-specific logout)
 * - Not Ready: Refresh token (for axios interceptor)
 * - Initialize auth (on app load)
 * - Not Ready:Submit MFA code (for Cognito/Local)
 */
export const useAuth = () => {
    const history = useHistory();
    const {
        storeData: { organization },
        storeDispatchActions,
    } = useAppContext();

    // Get actions without subscribing - they're stable references
    // We check isInitializing inside initializeAuth using getState() to avoid subscription
    const setIsAuthenticated = useAuthStore(
        (state) => state.setIsAuthenticated
    );
    const setTokens = useAuthStore((state) => state.setTokens);
    const clearTokens = useAuthStore((state) => state.clearTokens);
    const setInitializing = useAuthStore((state) => state.setInitializing);

    /**
     * Initialize authentication on app load
     * - Keycloak: Initialize and check SSO session
     * - Cognito/Local: Check for existing tokens (Zustand persist loads them automatically)
     */
    const initializeAuth = useCallback(async () => {
        try {
            if (APP_CONFIG.APP_IS_ONPREM) {
                // Keycloak initialization
                const authenticated = await keycloakService.init();
                if (authenticated) {
                    // Validate token is still valid
                    const validToken = await keycloakService.getValidToken();
                    if (validToken) {
                        const tokens = {
                            AccessToken: keycloakService.getToken(),
                            RefreshToken: keycloakService.getRefreshToken(),
                        };

                        setTokens(tokens);
                        setIsAuthenticated(true);
                        setInitializing(false);
                        return;
                    }
                }
            }

            // Zustand persist() already loaded tokens from localStorage
            // Just check the store's tokens (which were auto-loaded)
            const currentTokens = useAuthStore.getState().tokens;

            if (currentTokens?.AccessToken) {
                setIsAuthenticated(true);
            } else {
                clearTokens();
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error("Auth initialization error:", error);
            clearTokens();
        } finally {
            setInitializing(false);
        }
    }, [
        setInitializing,
        setTokens,
        clearTokens,
        setIsAuthenticated,
    ]);

    /**
     * Sign out - handles provider-specific logout
     * - Keycloak: Calls keycloakService.logout()
     * - Cognito/Local: Just clears tokens
     * Also clears app state and redirects to home
     */
    const signOut = useCallback(async () => {
        const cacheOrgId: string = organization.currentOrgId;
        let keycloakLogoutRedirect = false;

        try {
            // Clear auth tokens
            clearTokens();

            // Reset app resources
            useResourceStore.getState().resetResources();

            // Restore app data
            storeDispatchActions(restoreData());

            // Clear all localStorage
            LocalStorageService.clearAll();

            LocalStorageService.setItem("lastLoggedOrgId", cacheOrgId);

            // Logout from provider
            if (APP_CONFIG.APP_IS_ONPREM) {
                keycloakService.logout();
                keycloakLogoutRedirect = true;
            }
        } catch (error) {
            console.error("Sign out error:", error);
        } finally {
            if (!keycloakLogoutRedirect) {
                history.push("/");
            }
        }
    }, [
        organization.currentOrgId,
        clearTokens,
        storeDispatchActions,
        history,
    ]);

    return {
        // Actions
        signOut,
        initializeAuth,
    };
};
